import { ReactNode } from "react";
import { DocumentKind } from "@bitory-gmbh/bsm";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import {
  ListRequest,
  useRltWorkOrderListQuery,
  WorkOrderOrderBy,
} from "@/services/backend/rlt/work-orders/api/work-order-api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { BranchName } from "@/services/backend/branch/components/branch-name";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { TableCard } from "@/shared/components/tables/table-card";
import { DateBetweenFilter } from "@/shared/components/filter/date-between-filter";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { FulfillmentStateFilter } from "@/services/backend/relationships/components/fulfillment-state-filter";
import { BranchFilter } from "@/services/backend/branch/components/branch-filter";
import { FulfillmentStateIcon } from "@/services/backend/relationships/components/fulfillment-state-icon";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";

export function WorkOrderTable({
  request,
  onRequestChange,
  renderRowLink,
  hideBorder = false,
  hidePagination = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (
    workOrderId: string,
    processId: string,
    documentKind: DocumentKind,
  ) => ReactNode;
  hideBorder?: boolean;
  hidePagination?: boolean;
}) {
  const { data: list, isLoading, error } = useRltWorkOrderListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("Ausführende NL")}
              </TableHead>
              <TableHead>{t("Teamleiter")}</TableHead>
              <TableHead>{t("Beginn")}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t("% erledigt")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={colCount} />}
            {error && <TableRtkErrorRow error={error} colSpan={colCount} />}
            {emptyList && <TableEmptyRow colSpan={colCount} />}
            {list?.items.map((workOrder) => (
              <TableRow key={workOrder.id}>
                <TableCell>
                  {renderRowLink(
                    workOrder.id,
                    workOrder.processId!,
                    workOrder.documentKind!,
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <FulfillmentStateIcon state={workOrder.state} />
                    <span className="ml-2">{t(workOrder.state)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <BranchName branchId={workOrder.executingBranchId} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {workOrder.teamLeaderId}
                </TableCell>
                <TableCell>
                  {formatDate(workOrder.beginDate)}
                  {", "}
                  {formatTime(workOrder.beginDate)}
                </TableCell>
                <TableCell>{workOrder.percentageCompleted} %</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) => {
            onRequestChange({ ...request, limit, offset });
          }}
          className="ml-auto"
        />
      )}
    </div>
  );
}

const colCount = 6;

export function WorkOrderTableFilters({
  request,
  onRequestChange,
  hideStateFilter = false,
  hideBranchFilter = false,
  hideBeginDateFilter = false,
  hideEndDateFilter = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  hideStateFilter?: boolean;
  hideBranchFilter?: boolean;
  hideBeginDateFilter?: boolean;
  hideEndDateFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <FulfillmentStateFilter
        filter={request.state}
        onFilterChange={(state) => onRequestChange({ ...request, state })}
        hidden={hideStateFilter}
      />
      <BranchFilter
        filter={request.executingBranch}
        onFilterChange={(executingBranch) =>
          onRequestChange({ ...request, executingBranch })
        }
        hidden={hideBranchFilter}
      />
      <DateBetweenFilter
        label={t("Begin der Arbeiten")}
        filter={request.beginDate}
        onFilterChange={(beginDate) =>
          onRequestChange({ ...request, beginDate })
        }
        hidden={hideBeginDateFilter}
      />
      <DateBetweenFilter
        label={t("Ende der Arbeiten")}
        filter={request.endDate}
        onFilterChange={(endDate) => onRequestChange({ ...request, endDate })}
        hidden={hideEndDateFilter}
      />
      <OrderBySelect<WorkOrderOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Status"), value: "state" },
          { label: t("Begin der Arbeiten"), value: "beginDate" },
          { label: t("Ende der Arbeiten"), value: "endDate" },
          { label: t("% Erledigt"), value: "percentageCompleted" },
          { label: t("Teamleiter"), value: "teamLeader" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Bearbeitet am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}
